import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { flatten, formatGeneric, formatTranslations, getDate } from '../dataParsing/searchItemFormatting';
import { CREATE_LEADERBOARD_AUDIENCE, CREATE_LEADERBOARD, DELETE_LEADERBOARD, DELETE_LEADERBOARD_AUDIENCE, GET_LEADERBOARD, GET_LEADERBOARD_AUDIENCE, GET_LEADERBOARD_AUDIENCES, GET_LEADERBOARD_AUDIENCES_MINIMUM, GET_LEADERBOARD_TYPES, PATCH_LEADERBOARD, PATCH_LEADERBOARD_AUDIENCE } from '../../../gql/queries/LEADERBOARDS';
import { useEffect, useMemo, useState } from 'react';

function formatAudience( a ) {
    return {
        ...a,
        id: a.leaderboardAudienceId,
        locales: a.locales ? formatTranslations( a.locales ) : undefined,
        originalData: a,
    };
}

export function getLeaderboardGranularity( g ) {
    const all = { key: 'ALL', name: 'All Time', color: 'green' }, 
        monthly = { key: 'MONTHLY', name: 'Monthly', color: 'purple' }, 
        yearly = { key: 'YEARLY', name: 'Yearly', color: 'yellow' };

    if ( g === all.key ) {
        return [ all ];
    }
    if ( g === yearly.key ) {
        return [ all, yearly ];
    }
    if ( g === monthly.key ) {
        return [ all, yearly, monthly ];
    }
    return [];
}

function formatLeaderboardQueryItem( l ) {

    return {
        ...l,
        id: l.leaderboardId,
        endAt: getDate( l.endAt ),
        locales: formatTranslations( l.locales ),
        granularities: getLeaderboardGranularity( l.granularity ),
        startAt: getDate( l.startAt ),
        originalData: l,
    };
}

export const useLeaderboardQuery = () => {

    const [ fetchLeaderboard ] = useLazyQuery( GET_LEADERBOARD, { fetchPolicy: 'no-cache' } );
    const { fetchLeaderboardAudiences } = useLeaderboardAudiencesQuery();

    async function fetch( { id } ) {
        const variables = {
            leaderboardId: id
        };

        const [ result, audiences ] = [ await fetchLeaderboard( { variables } ), await fetchLeaderboardAudiences( { leaderboardId: id } ) ];

        const data = formatLeaderboardQueryItem( formatGeneric( flatten( result.data.leaderboard ) ) );
        data.audiences = audiences.data;

        return { data };
    }

    return [ fetch ];
};

export const useCreateLeaderboardMutation = () => {

    const [ createLeaderboard ] = useMutation( CREATE_LEADERBOARD );

    async function create( leaderboard ) {
        const variables = {
            input: leaderboard
        };

        const result = await createLeaderboard( { variables } );

        const data = formatLeaderboardQueryItem( formatGeneric( flatten( result.data.createLeaderboard ) ) );

        return { data };
    }

    return [ create ];
};

export const usePatchLeaderboardMutation = () => {

    const [ patchLeaderboard ] = useMutation( PATCH_LEADERBOARD );

    async function patch( leaderboard ) {
        const variables = {
            input: leaderboard
        };

        const result = await patchLeaderboard( { variables } );

        const data = formatLeaderboardQueryItem( formatGeneric( flatten( result.data.patchLeaderboard ) ) );

        return { data };
    }

    return [ patch ];
};

export const useDeleteLeaderboardMutation = () => {

    const [ deleteLeaderboardMutation ] = useMutation( DELETE_LEADERBOARD );

    async function deleteLeaderboard( leaderboardId ) {
        const variables = {
            input: {
                leaderboardId
            }
        };

        await deleteLeaderboardMutation( { variables } );

        return { success: true };
    }

    return [ deleteLeaderboard ];
};

export const useLeaderboardAudiencesQuery = () => {

    const [ fetchAudiencesQuery ] = useLazyQuery( GET_LEADERBOARD_AUDIENCES, { fetchPolicy: 'no-cache' } );
    const [ fetchAudiencesMinQuery ] = useLazyQuery( GET_LEADERBOARD_AUDIENCES_MINIMUM, { fetchPolicy: 'no-cache' } );

    /**
     * Gets all audiences for a single leaderboard
     * @param {UUID} leaderboardId leaderboard ID
     * @param {boolean?} minimum whether to use the minimum audience query. default false
     * @returns all audiences for leaderboard
     */
    async function fetchLeaderboardAudiences( { leaderboardId, minimum = false } ) {
        try {
            const variables = {
                leaderboardId
            };
            const result = minimum ? await fetchAudiencesMinQuery( { variables } ) : await fetchAudiencesQuery( { variables } );
    
            if ( result.error ) {
                throw result.error;
            }
            const data = flatten( result.data.leaderboardAudiences ).map( formatGeneric ).map( formatAudience );

            return { success: true, data };
        } catch ( e ) {
            return { success: false, errors: e };
        }
    }

    /**
     * Gets all audiences for listed leaderboards
     * @param {UUID[]} leaderboardIds leaderboard IDs
     * @param {boolean?} minimum whether to use the minimum audience query. default true due to large processing volume
     * @returns all audiences for list of leaderboards
     */
    async function fetchLeaderboardsAudiences( { leaderboardIds, minimum = true } ) {
        const results = await Promise.all( leaderboardIds.map( id => fetchLeaderboardAudiences( { leaderboardId: id, minimum } ) ) );
        const data = results.flatMap( r => r.data );

        if ( data.length === new Set( leaderboardIds ).length && results.every( r => r.success ) ) {
            return { success: true, data };
        }

        return { success: false, data };
    }

    return { fetchLeaderboardAudiences, fetchLeaderboardsAudiences };
};

export const useLeaderboardAudienceQuery = () => {

    const [ fetchAudienceQuery ] = useLazyQuery( GET_LEADERBOARD_AUDIENCE, { fetchPolicy: 'no-cache' } );

    /**
     * Gets one leaderboard audience
     * @param {UUID} leaderboardId leaderboard ID
     * @param {UUID} audienceId audience ID
     * @returns leaderboard audience
     */
    async function fetchAudience( { leaderboardId, audienceId } ) {
        try {
            const variables = {
                leaderboardId, leaderboardAudienceId: audienceId, // ts: new Date().getTime()
            };
            const result = await fetchAudienceQuery( { variables } );
    
            if ( result.error ) {
                throw result.error;
            }
            const data = formatAudience( formatGeneric( flatten( result.data.leaderboardAudience ) ) );

            return { success: true, data };
        } catch ( e ) {
            return { success: false, errors: e };
        }
    }

    return [ fetchAudience ];
};

/**
 * CUD operations for audiences
 * @returns audience create, edit, delete mutations in format [saveAllChanges, {create, deleteAudiences, patch}]
 */
export const useSaveLeaderboardAudiencesMutations = () => {
    const [ patchAudienceMutation ] = useMutation( PATCH_LEADERBOARD_AUDIENCE );
    const [ deleteAudienceMutation ] = useMutation( DELETE_LEADERBOARD_AUDIENCE );
    const [ createAudienceMutation ] = useMutation( CREATE_LEADERBOARD_AUDIENCE );

    async function create( audience ) {

        try {
            const result = await createAudienceMutation( { variables: { input: audience } } );
    
            if ( result.errors ) {
                throw result.errors;
            }
            const data = formatAudience( formatGeneric( result.data.createLeaderboardAudience ) );

            return { success: true, operation: `Create ${ audience.leaderboardAudienceId }`, data };
        } catch ( e ) {
            return { success: false, errors: e, operation: 'Create audience' };
        }
    }

    async function deleteAudiences( leaderboardId, leaderboardAudienceId ) {

        try {
            const result = await deleteAudienceMutation( { variables: { input: { leaderboardId, leaderboardAudienceId } } } );
    
            if ( result.errors ) {
                throw result.errors;
            }

            return { success: true, operation: `Delete ${ leaderboardAudienceId }` };
        } catch ( e ) {
            return { success: false, errors: e, operation: 'Delete audience' };
        }
    }

    async function patch( audience ) {

        try {
            const result = await patchAudienceMutation( { variables: { input: audience } } );
    
            if ( result.errors ) {
                throw result.errors;
            }

            const data = formatAudience( formatGeneric( result.data.patchLeaderboardAudience ) );

            return { success: true, operation: `Patch ${ audience.id }`, data };
        } catch ( e ) {
            return { success: false, errors: e, operation: 'Patch audience' };
        }
    }

    async function save( { creates, deleteIds, patches } ) {
        const result = await Promise.all( [
            ...( creates?.map( c => create( c ) ) ?? [] ),
            ...( deleteIds?.map( d => deleteAudiences( d ) ) ?? [] ),
            ...( patches?.map( p => patch( p ) ) ?? [] ),
        ] );

        const ret = { success: result.every( r => r.success ), results: result.map( r => r.data ) };

        return ret;
    }

    return [ save, { create, deleteAudiences, patch } ];
};

/**
 * Gets leaderyboard types
 * @returns list of leaderboard types in format [{leaderboardTypeId, name}]
 */
export const useLeaderboardTypesQuery = () => {

    const { data, loading } = useQuery( GET_LEADERBOARD_TYPES );

    const [ ret, setRet ] = useState( null );

    useEffect( () => {
        if ( loading ) {
            setRet( null );
        } else if ( !loading && data ) {
            setRet( flatten( data.leaderboardTypes ) );
        } else {
            setRet( [] );
        }
    }, [ data, loading ] );

    return useMemo( () => ret, [ ret ] );
};